import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import DatePicker from './DatePicker';
import api from '../utils/api';

const EXPENSE_CATEGORIES = [
  // State Filing Categories
  { value: 'state_filing_fee', label: 'State Filing Fee' },
  { value: 'annual_report_fee', label: 'Annual Report Fee' },
  { value: 'foreign_qualification_fee', label: 'Foreign Qualification Fee' },
  { value: 'name_change_fee', label: 'Name Change Fee' },
  { value: 'good_standing_certificate_fee', label: 'Good Standing Certificate' },
  { value: 'reinstatement_fee', label: 'Reinstatement Fee' },
  { value: 'amendment_fee', label: 'Amendment Fee' },
  
  // Licensing & Renewal Categories
  { value: 'license_application_fee', label: 'License Application Fee' },
  { value: 'license_renewal_fee', label: 'License Renewal Fee' },
  { value: 'ancillary_registration_fee', label: 'Ancillary Registration Fee' },
  { value: 'fingerprinting_fee', label: 'Fingerprinting Fee' },
  { value: 'background_check_fee', label: 'Background Check Fee' },
  { value: 'regulatory_fee', label: 'Regulatory Fee' },
  { value: 'continuing_education_fee', label: 'Continuing Education Fee' },
  
  // DBA & Name Registration Categories
  { value: 'dba_registration_fee', label: 'DBA Registration Fee' },
  { value: 'dba_renewal_fee', label: 'DBA Renewal Fee' },
  { value: 'publication_fee', label: 'Publication Fee' },
  { value: 'trade_name_fee', label: 'Trade Name Fee' },
  { value: 'assumed_name_fee', label: 'Assumed Name Fee' },
  { value: 'fictitious_name_fee', label: 'Fictitious Name Fee' },
  
  // Other
  { value: 'legal_fee', label: 'Legal Fee' },
  { value: 'consulting_fee', label: 'Consulting Fee' },
  { value: 'other', label: 'Other' }
];

const PAYMENT_METHODS = [
  'Credit Card',
  'ACH Transfer',
  'Wire Transfer',
  'Check',
  'Cash',
  'Other'
];

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const AddExpenseDialog = ({ open, onClose, onSuccess, expense = null }) => {
  // Edit mode only if expense has an expense_id (existing expense)
  // If expense is provided but no expense_id, it's pre-filled data for CREATE mode (from prompt)
  const isEditMode = expense && expense.expense_id;
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    state: '',
    category: '',
    subcategory: '',
    vendor: '',
    amount: '',
    payment_date: '',
    due_date: '',
    status: 'pending',
    payment_method: '',
    reference_number: '',
    invoice_number: '',
    entity_id: '',
    license_id: '',
    dba_id: '',
    filing_id: '',
    task_id: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [entities, setEntities] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [dbas, setDbas] = useState([]);
  const [loadingCompliance, setLoadingCompliance] = useState(false);

  // Load compliance objects when dialog opens
  useEffect(() => {
    if (open) {
      loadComplianceObjects();
    }
  }, [open]);

  const loadComplianceObjects = async () => {
    setLoadingCompliance(true);
    try {
      // Load entities, licenses, and DBAs in parallel
      const [entitiesResponse, licensesResponse, dbasResponse] = await Promise.all([
        api.getEntities(),
        api.getLicenses(),
        api.getDBAs()
      ]);
      
      setEntities(entitiesResponse.entities || []);
      setLicenses(licensesResponse.licenses || []);
      setDbas(dbasResponse.dbas || []);
    } catch (error) {
      console.error('Failed to load compliance objects:', error);
    } finally {
      setLoadingCompliance(false);
    }
  };

  useEffect(() => {
    if (expense) {
      // Helper to convert ISO date to MM/DD/YYYY
      const convertISOtoMMDDYYYY = (isoDate) => {
        if (!isoDate) return '';
        const date = new Date(isoDate);
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const year = date.getFullYear();
        return `${month}/${day}/${year}`;
      };

      setFormData({
        title: expense.title || '',
        description: expense.description || '',
        state: expense.state || '',
        category: expense.category || '',
        subcategory: expense.subcategory || '',
        vendor: expense.vendor || '',
        amount: expense.amount || '',
        payment_date: expense.payment_date ? convertISOtoMMDDYYYY(expense.payment_date) : '',
        due_date: expense.due_date ? convertISOtoMMDDYYYY(expense.due_date) : '',
        status: expense.status || 'pending',
        payment_method: expense.payment_method || '',
        reference_number: expense.reference_number || '',
        invoice_number: expense.invoice_number || '',
        entity_id: expense.entity_id || '',
        license_id: expense.license_id || '',
        dba_id: expense.dba_id || '',
        filing_id: expense.filing_id || '',
        task_id: expense.task_id || ''
      });
    } else {
      // Reset for add mode
      setFormData({
        title: '',
        description: '',
        state: '',
        category: '',
        subcategory: '',
        vendor: '',
        amount: '',
        payment_date: '',
        due_date: '',
        status: 'pending',
        payment_method: '',
        reference_number: '',
        invoice_number: '',
        entity_id: '',
        license_id: '',
        dba_id: '',
        filing_id: '',
        task_id: ''
      });
    }
  }, [expense, open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('[AddExpenseDialog] Submit started', { isEditMode, formData });
    
    if (!formData.title || !formData.category || !formData.amount) {
      alert('Please fill in required fields: Title, Category, and Amount');
      return;
    }

    setLoading(true);
    try {
      // Helper to convert MM/DD/YYYY to ISO date
      const convertMMDDYYYYtoISO = (mmddyyyy) => {
        if (!mmddyyyy || mmddyyyy.length !== 10) return null;
        const [month, day, year] = mmddyyyy.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const expenseData = {
        ...formData,
        amount: parseFloat(formData.amount),
        payment_date: formData.payment_date ? convertMMDDYYYYtoISO(formData.payment_date) : null,
        due_date: formData.due_date ? convertMMDDYYYYtoISO(formData.due_date) : null
      };

      // Remove empty fields and convert "none" to empty string
      Object.keys(expenseData).forEach(key => {
        if (expenseData[key] === '' || expenseData[key] === null || expenseData[key] === 'none') {
          delete expenseData[key];
        }
      });

      console.log('[AddExpenseDialog] Submitting expense data', { isEditMode, expenseData, expense_id: expense?.expense_id });

      if (isEditMode) {
        console.log('[AddExpenseDialog] UPDATE mode - calling api.updateExpense', expense.expense_id);
        await api.updateExpense(expense.expense_id, expenseData);
        console.log('[AddExpenseDialog] Update successful');
      } else {
        console.log('[AddExpenseDialog] CREATE mode - calling api.createExpense');
        const result = await api.createExpense(expenseData);
        console.log('[AddExpenseDialog] Create successful, result:', result);
      }

      console.log('[AddExpenseDialog] About to call onSuccess and onClose');
      
      if (onSuccess) {
        console.log('[AddExpenseDialog] Calling onSuccess');
        await onSuccess();
      }
      
      if (onClose) {
        console.log('[AddExpenseDialog] Calling onClose');
        onClose();
      }
      
      console.log('[AddExpenseDialog] Success callbacks completed');
    } catch (error) {
      console.error('[AddExpenseDialog] Failed to save expense:', error);
      console.error('[AddExpenseDialog] Error details:', error.message, error.stack);
      alert(`Failed to ${isEditMode ? 'update' : 'create'} expense: ${error.message}`);
    } finally {
      console.log('[AddExpenseDialog] Setting loading to false');
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="add-expense-dialog">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Edit Expense' : 'Add New Expense'}</DialogTitle>
          <DialogDescription>
            {isEditMode ? 'Update expense details' : 'Record a new compliance-related expense'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit}>
          <div className="space-y-6 py-4">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Basic Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <Label htmlFor="title">Title <span className="text-destructive">*</span></Label>
                  <Input
                    id="title"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Florida Annual Report Filing Fee"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="category">Category <span className="text-destructive">*</span></Label>
                  <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                    <SelectTrigger id="category">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent className="max-h-80">
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground">State Filing</div>
                      {EXPENSE_CATEGORIES.slice(0, 7).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">Licensing & Renewal</div>
                      {EXPENSE_CATEGORIES.slice(7, 14).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">DBA & Name Registration</div>
                      {EXPENSE_CATEGORIES.slice(14, 20).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                      <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground border-t mt-1">Other</div>
                      {EXPENSE_CATEGORIES.slice(20).map(cat => (
                        <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="subcategory">Subcategory</Label>
                  <Input
                    id="subcategory"
                    value={formData.subcategory}
                    onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                    placeholder="Optional subcategory"
                  />
                </div>

                <div>
                  <Label htmlFor="state">State</Label>
                  <Select value={formData.state} onValueChange={(value) => setFormData({ ...formData, state: value })}>
                    <SelectTrigger id="state">
                      <SelectValue placeholder="Select state" />
                    </SelectTrigger>
                    <SelectContent>
                      {US_STATES.map(state => (
                        <SelectItem key={state} value={state}>{state}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="amount">Amount <span className="text-destructive">*</span></Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                    <Input
                      id="amount"
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      className="pl-7"
                      placeholder="0.00"
                      required
                    />
                  </div>
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Additional notes or details about this expense"
                    rows={3}
                  />
                </div>
              </div>
            </div>

            {/* Payment Information */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Payment Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="vendor">Vendor/Payee</Label>
                  <Input
                    id="vendor"
                    value={formData.vendor}
                    onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                    placeholder="e.g., Florida Department of State"
                  />
                </div>

                <div>
                  <Label htmlFor="payment_method">Payment Method</Label>
                  <Select value={formData.payment_method} onValueChange={(value) => setFormData({ ...formData, payment_method: value })}>
                    <SelectTrigger id="payment_method">
                      <SelectValue placeholder="Select method" />
                    </SelectTrigger>
                    <SelectContent>
                      {PAYMENT_METHODS.map(method => (
                        <SelectItem key={method} value={method}>{method}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="payment_date">Payment Date</Label>
                  <DatePicker
                    id="payment_date"
                    value={formData.payment_date}
                    onChange={(value) => setFormData({ ...formData, payment_date: value })}
                    placeholder="MM/DD/YYYY"
                  />
                </div>

                <div>
                  <Label htmlFor="due_date">Due Date</Label>
                  <DatePicker
                    id="due_date"
                    value={formData.due_date}
                    onChange={(value) => setFormData({ ...formData, due_date: value })}
                    placeholder="MM/DD/YYYY"
                  />
                </div>

                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                    <SelectTrigger id="status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="paid">Paid</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="reimbursed">Reimbursed</SelectItem>
                      <SelectItem value="voided">Voided</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="reference_number">Reference Number</Label>
                  <Input
                    id="reference_number"
                    value={formData.reference_number}
                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                    placeholder="Transaction or confirmation #"
                  />
                </div>

                <div className="md:col-span-2">
                  <Label htmlFor="invoice_number">Invoice Number</Label>
                  <Input
                    id="invoice_number"
                    value={formData.invoice_number}
                    onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                    placeholder="Invoice or bill number"
                  />
                </div>
              </div>
            </div>

            {/* Compliance Object Linking */}
            <div className="space-y-4 pt-4 border-t">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Compliance Object Linking (Optional)</h3>
              <p className="text-xs text-muted-foreground">Link this expense to a specific compliance record for better tracking and reporting</p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="entity_id">Entity</Label>
                  <Select value={formData.entity_id} onValueChange={(value) => setFormData({ ...formData, entity_id: value })}>
                    <SelectTrigger id="entity_id">
                      <SelectValue placeholder="Select entity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {loadingCompliance ? (
                        <SelectItem value="loading" disabled>Loading entities...</SelectItem>
                      ) : (
                        entities.map(entity => (
                          <SelectItem key={entity.entity_id} value={entity.entity_id}>
                            {entity.legal_name || entity.entity_name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="license_id">License</Label>
                  <Select value={formData.license_id} onValueChange={(value) => setFormData({ ...formData, license_id: value })}>
                    <SelectTrigger id="license_id">
                      <SelectValue placeholder="Select license" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {loadingCompliance ? (
                        <SelectItem value="loading" disabled>Loading licenses...</SelectItem>
                      ) : (
                        licenses.map(license => (
                          <SelectItem key={license.license_id} value={license.license_id}>
                            {license.license_type} - {license.state}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="dba_id">DBA / Trade Name</Label>
                  <Select value={formData.dba_id} onValueChange={(value) => setFormData({ ...formData, dba_id: value })}>
                    <SelectTrigger id="dba_id">
                      <SelectValue placeholder="Select DBA" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {loadingCompliance ? (
                        <SelectItem value="loading" disabled>Loading DBAs...</SelectItem>
                      ) : (
                        dbas.map(dba => (
                          <SelectItem key={dba.dba_id} value={dba.dba_id}>
                            {dba.dba_name} - {dba.state}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="task_id">Related Task ID</Label>
                  <Input
                    id="task_id"
                    value={formData.task_id}
                    onChange={(e) => setFormData({ ...formData, task_id: e.target.value })}
                    placeholder="Enter task ID if applicable"
                  />
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
            <Button 
              type="button"
              disabled={loading} 
              data-testid="save-expense-button"
              onClick={async (e) => {
                console.log('[Button Click] Submit button clicked - calling handleSubmit directly');
                e.preventDefault();
                await handleSubmit(e);
              }}
            >
              {loading ? 'Saving...' : (isEditMode ? 'Update Expense' : 'Create Expense')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddExpenseDialog;
